"use client"

import { motion } from "framer-motion";
import {
    Activity,
    Brain,
    Bug,
    ChartLine,
    ClipboardList,
    ShieldCheck,
    Workflow,
} from "lucide-react";
import { Products } from "@/types/productsTypes";

const iconMap = {
    workflow: Workflow,
    shield: ShieldCheck,
    brain: Brain,
    bug: Bug,
    analytics: ChartLine,
    checklist: ClipboardList,
    activity: Activity,
} as const;

export function Capabilities({ section }: { section: Products["atlasai"]["capabilities"] }) {
    return (
        <section className="section-bg-darker rounded-[32px] px-8 py-24">
            <div className="max-w-6xl mx-auto space-y-12">
                <div className="text-center space-y-4">
                    <span className="section-badge badge-size inline-block w-fit">
                        {section.badge}
                    </span>
                    <h2 className="title-size font-semibold text-bg">
                        {section.title}
                    </h2>
                    <p className="text-size text-muted-foreground max-w-3xl mx-auto">
                        {section.description}
                    </p>
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                    {section.cards.map((card, index) => {
                        const Icon = card.icon ? iconMap[card.icon as keyof typeof iconMap] : ShieldCheck;
                        return (
                            <motion.div
                                key={card.title}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                className="rounded-3xl border bg-white p-6 shadow-sm"
                            >
                                <div className="iconBudg-v2 size-11 mb-4">
                                    <Icon className="size-6" />
                                </div>
                                <h3 className="text-xl font-semibold text-bg mb-3">
                                    {card.title}
                                </h3>
                                {card.bullets ? (
                                    <ul className="space-y-2 text-sm text-muted-foreground">
                                        {card.bullets.map((bullet) => (
                                            <li key={bullet} className="flex items-start gap-2">
                                                <span className="mt-1 size-2 rounded-full bg-primary-500" />
                                                {bullet}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-sm text-muted-foreground">
                                        {card.description}
                                    </p>
                                )}
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
